import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  UserCog, Mail, ShieldCheck, Calendar, LogOut, Save, KeyRound, Loader2,
  Sun, Moon, Chrome, AtSign, Camera, Building2, ImagePlus, Trash2,
} from "lucide-react";

const Card = ({ title, icon: Icon, desc, children }) => (
  <div className="rounded-2xl border border-border bg-card p-6" data-testid={`card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-4 w-4 text-primary" />
      <h2 className="font-bold">{title}</h2>
    </div>
    {desc && <p className="text-xs text-muted-foreground mb-4">{desc}</p>}
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme !== "light";
  const isEmailAuth = (user?.auth_provider || "email") === "email";

  const avatarRef = useRef();
  const logoRef = useRef();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState({ company_name: user?.company_name || "", company_address: user?.company_address || "", company_license: user?.company_license || "" });
  const [savingName, setSavingName] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [busyAvatar, setBusyAvatar] = useState(false);
  const [busyLogo, setBusyLogo] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    setSavingName(true);
    try {
      const { data } = await api.patch("/users/me", { name: trimmed });
      setUser((u) => ({ ...u, ...data }));
      toast.success("Profile updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingName(false); }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const { data } = await api.patch("/users/me", { name: (name || user?.name || "").trim() || "User", company_name: company.company_name, company_address: company.company_address, company_license: company.company_license });
      setUser((u) => ({ ...u, ...data }));
      toast.success("Company profile saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingCompany(false); }
  };

  const uploadImage = async (file, kind) => {
    if (!file) return;
    if (!/(jpe?g|png|webp)$/i.test(file.name)) { toast.error("Use a JPG, PNG or WebP image."); return; }
    const setBusy = kind === "avatar" ? setBusyAvatar : setBusyLogo;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/users/me/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser((u) => ({ ...u, ...data }));
      toast.success(kind === "avatar" ? "Profile photo updated" : "Logo uploaded");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.delete("/users/me/logo");
      setUser((u) => ({ ...u, ...data }));
      toast.success("Logo removed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const savePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setSavingPw(true);
    try {
      await api.post("/users/me/password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingPw(false); }
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><UserCog className="h-6 w-6 text-primary" /> Profile & Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account details, company branding, security and appearance.</p>
      </div>

      {/* Account overview */}
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="account-overview">
        <div className="flex items-center gap-4">
          <button data-testid="avatar-upload-btn" onClick={() => avatarRef.current?.click()}
            className="relative group h-16 w-16 rounded-full bg-primary/20 text-primary grid place-items-center text-2xl font-bold overflow-hidden shrink-0"
            aria-label="Upload profile photo">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U").toUpperCase()}
            <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
              {busyAvatar ? <Loader2 className="h-5 w-5 text-white animate-spin" /> : <Camera className="h-5 w-5 text-white" />}
            </span>
          </button>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" data-testid="avatar-file-input" onChange={(e) => uploadImage(e.target.files?.[0], "avatar")} />
          <div className="min-w-0">
            <div className="text-lg font-bold truncate" data-testid="profile-name">{user?.name}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{user?.email}</div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider" data-testid="role-badge">
                <ShieldCheck className="h-3 w-3" />{user?.role || "user"}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground" data-testid="provider-badge">
                {isEmailAuth ? <><AtSign className="h-3 w-3" /> Email sign-in</> : <><Chrome className="h-3 w-3" /> Google sign-in</>}
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Calendar className="h-3 w-3" /> Member since {memberSince}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit profile */}
      <Card title="Account Details" icon={UserCog} desc="Update how your name appears across estimates and proposals.">
        <div className="space-y-4">
          <Field label="Full name">
            <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Your name" />
          </Field>
          <Field label="Email">
            <input value={user?.email || ""} disabled className={`${inputCls} opacity-60 cursor-not-allowed`} />
          </Field>
          <div className="flex justify-end">
            <button data-testid="save-profile-btn" onClick={saveName} disabled={savingName || name.trim() === (user?.name || "")}
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50">
              {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        </div>
      </Card>

      {/* Company profile */}
      <Card title="Company Profile" icon={Building2} desc="Your company name, logo and address auto-fill onto client proposals and PDF exports.">
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="h-20 w-32 rounded-lg border border-border bg-background grid place-items-center overflow-hidden shrink-0" data-testid="logo-preview">
              {user?.company_logo_b64
                ? <img src={user.company_logo_b64} alt="Company logo" className="h-full w-full object-contain p-1" />
                : <ImagePlus className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="flex flex-col gap-2">
              <button data-testid="logo-upload-btn" onClick={() => logoRef.current?.click()} disabled={busyLogo}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
                {busyLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {user?.company_logo_b64 ? "Replace logo" : "Upload logo"}
              </button>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={(e) => uploadImage(e.target.files?.[0], "logo")} />
              {user?.company_logo_b64 && (
                <button data-testid="logo-remove-btn" onClick={removeLogo} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline">
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
              <span className="text-[11px] text-muted-foreground">PNG or JPG, shown on proposals.</span>
            </div>
          </div>
          <Field label="Company name">
            <input data-testid="company-name-input" value={company.company_name} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} className={inputCls} placeholder="e.g. Cornerstone Builders LLC" />
          </Field>
          <Field label="Business address">
            <textarea data-testid="company-address-input" value={company.company_address} onChange={(e) => setCompany({ ...company, company_address: e.target.value })} rows={3} className={`${inputCls} resize-y`} placeholder="Street, City, State ZIP · Phone" />
          </Field>
          <Field label="Contractor license #">
            <input data-testid="company-license-input" value={company.company_license} onChange={(e) => setCompany({ ...company, company_license: e.target.value })} className={inputCls} placeholder="e.g. TX GC-123456 — shown as 'Licensed Contractor' on proposals" />
          </Field>
          <div className="flex justify-end">
            <button data-testid="save-company-btn" onClick={saveCompany} disabled={savingCompany}
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50">
              {savingCompany ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save company
            </button>
          </div>
        </div>
      </Card>

      {/* Security */}
      <Card title="Security" icon={KeyRound} desc={isEmailAuth ? "Change your password. Use at least 8 characters." : "Password is managed by your Google account."}>
        {isEmailAuth ? (
          <div className="space-y-4">
            <Field label="Current password">
              <input data-testid="current-password-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} autoComplete="current-password" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="New password">
                <input data-testid="new-password-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} autoComplete="new-password" />
              </Field>
              <Field label="Confirm new password">
                <input data-testid="confirm-password-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} autoComplete="new-password" />
              </Field>
            </div>
            <div className="flex justify-end">
              <button data-testid="save-password-btn" onClick={savePassword} disabled={savingPw || !pw.current_password || !pw.new_password}
                className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50">
                {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground" data-testid="google-security-note">
            You signed in with Google — manage your password from your Google account.
          </div>
        )}
      </Card>

      {/* Appearance */}
      <Card title="Appearance" icon={isDark ? Moon : Sun} desc="Switch between light and dark themes.">
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium">{isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} {isDark ? "Dark" : "Light"} mode</div>
          <button data-testid="profile-theme-toggle" onClick={() => setTheme(isDark ? "light" : "dark")}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary transition-colors">
            Switch to {isDark ? "light" : "dark"}
          </button>
        </div>
      </Card>

      {/* Sign out */}
      <div className="flex justify-end">
        <button data-testid="profile-logout-btn" onClick={doLogout}
          className="inline-flex items-center gap-2 rounded-lg border border-destructive/40 text-destructive px-4 py-2 text-sm font-semibold hover:bg-destructive/10 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
}
